import { openDB, deleteDB, IDBPDatabase } from 'idb'

// Do not import any stores here, persistence store needs to be the first load

import createError from 'utils/createError'

import PersistenceItem from './PersistenceItem'
import migrate from './migrations'
import {
  ITableName,
  IDBSchema,
  IProjectSchema,
  ISessionSchema,
  IScriptSchema,
  ITabSchema,
  IActionSchema,
  ISchema,
} from './types'

class PersistenceStore {
  private databaseName = 'Prisma Studio'
  private indexedDBVersion: number = 13
  private databaseInstance: IDBPDatabase<IDBSchema> | null = null
  private projectId: string = ''

  public ready: boolean = false
  // We're adding `!` everywhere because `items` is assigned asynchronously in `init`. It is also guaranteed by BootstrapStore, but I cannot represent this using only typescript
  public projects!: PersistenceItem<IProjectSchema>
  public tabs!: PersistenceItem<ITabSchema>
  public sessions!: PersistenceItem<ISessionSchema>
  public scripts!: PersistenceItem<IScriptSchema>
  public actions!: PersistenceItem<IActionSchema>

  /**
   * Loads all state of a single project from IndexedDB
   */
  init = async ({ projectId }: { projectId: string }) => {
    try {
      this.projectId = projectId
      this.databaseInstance = await openDB<IDBSchema>(
        this.databaseName,
        this.indexedDBVersion,
        {
          upgrade: migrate,
        }
      )

      // Each of the following is a reference to an IndexedDB table
      this.projects = new PersistenceItem('projects', this.databaseInstance)
      this.tabs = new PersistenceItem('tabs', this.databaseInstance)
      this.sessions = new PersistenceItem('sessions', this.databaseInstance)
      this.scripts = new PersistenceItem('scripts', this.databaseInstance)
      this.actions = new PersistenceItem('actions', this.databaseInstance)

      // Mark the DB as ready
      this.ready = true
    } catch (e) {
      // Rethrow this error so consumer can handle it
      throw createError(
        'PersistenceStore.init',
        'Unable to init PersistenceStore',
        {},
        e
      )
    }
  }

  /**
   * Saves an item to IndexedDB if its table exists
   */
  save = (tableName: ITableName, value: ISchema): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      if (!this.ready) {
        throw createError(
          'PersistenceStore.save',
          'PersistenceStore is not ready to receive `save` operations yet',
          {
            tableName,
            value,
          }
        )
      }

      try {
        switch (tableName) {
          case 'sessions':
            await this.sessions.create(value as ISessionSchema)
            break
          case 'scripts':
            await this.scripts.create(value as IScriptSchema)
            break
          case 'tabs':
            await this.tabs.create(value as ITabSchema)
            break
          case 'projects':
            await this.projects.create(value as IProjectSchema)
            break
          case 'actions':
            await this.actions.create(value as IActionSchema)
            break
          default:
            break
        }

        resolve()
      } catch (e) {
        reject(e)
      }
    })
  }

  /**
   * Returns a table's rows, if it exists
   */
  load = async (tableName: ITableName): Promise<ISchema[]> => {
    return new Promise(async (resolve, reject) => {
      if (!this.ready) {
        throw createError(
          'PersistenceStore.load',
          'PersistenceStore is not ready to receive `load` operations yet',
          {
            tableName,
          }
        )
      }

      try {
        switch (tableName) {
          case 'projects':
            return resolve(await this.projects.getAll())
          default:
            return resolve(
              await this[tableName].getAll({ projectId: this.projectId })
            )
        }
      } catch (e) {
        return reject(e)
      }
    })
  }

  /**
   * Removes a table's row, if it exists
   */
  remove = async (tableName: ITableName, id: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      if (!this.ready) {
        throw createError(
          'PersistenceStore.remove',
          'PersistenceStore is not ready to receive `remove` operations yet',
          {
            tableName,
            id,
          }
        )
      }

      try {
        switch (tableName) {
          case 'projects':
            await this[tableName].delete(id)
            return resolve()
          default:
            await this[tableName].delete([id, this.projectId])
            return resolve()
        }
      } catch (e) {
        return reject(e)
      }
    })
  }

  /**
   * Clears a table
   */
  clear = async (tableName: ITableName): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      if (!this.ready) {
        throw createError(
          'PersistenceStore.clear',
          'PersistenceStore is not ready to receive `clear` operations yet',
          {
            tableName,
          }
        )
      }

      try {
        await this[tableName].clear()
        return resolve()
      } catch (e) {
        return reject(e)
      }
    })
  }

  /**
   * Removes the database (and all tables from IndexedDB)
   */
  clearAll = async () => {
    this.databaseInstance?.close()
    await deleteDB(this.databaseName)
  }
}

export default new PersistenceStore()
